import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Small back-arrow shown under the Navbar on every page except the homepage.
 * Uses browser history when available, otherwise falls back to "/".
 */
const BackButton = () => {
  const location = useLocation();
  const navigate = useNavigate();

  if (location.pathname === '/') return null;
  // iter229m · Hide the global Back on athlete profile pages — those
  // routes already render an in-card "Back to Network" button, so
  // showing both was a duplicate stack (see /network/{slug}).
  const isAthleteProfile = /^\/(?:visionary\/)?network\/[^/]+/.test(location.pathname);
  if (isAthleteProfile) return null;
  // iter454 · Also hide on the bare-slug athlete profile route
  // (flythouse.com/{slug} — see TopLevelAthleteRoute in App.js). The
  // AthleteProfileHero renders its own Back pill on top of the photo,
  // so surfacing the global one produces a duplicate stack.
  const isBareSlug = /^\/[^/]+\/?$/.test(location.pathname)
    && !location.pathname.match(/^\/(login|signup|profile|network|admin|stories|collections|brand|search|agent|scout|recruiter|dashboard|settings|for-athletes|for-agents|for-brands|for-programs|guides|school-partnerships|claim|l|texas-hs|visionary|inbox|onboarding|auth|api|founder|studios|company|help|terms|privacy)/);
  if (isBareSlug) return null;
  // iter816 · Jay: no back bar at all on the Network Profile screen — the
  // bottom-nav profile tab already IS the entry point for this page, so a
  // "go back" affordance (and its black strip / gap before the player
  // card) doesn't belong here.
  if (location.pathname.startsWith('/network-profile')) return null;

  // Network routes use a dark surface, so the back strip needs lighter text.
  // iter816 · `/network-profile` is a prefix-match false positive here —
  // it's the new LIGHT-background screen, not a dark `/network/:slug`
  // page. Require the trailing slash so only real network sub-routes
  // match.
  const isDarkRoute = location.pathname.startsWith('/network/')
    || location.pathname.startsWith('/visionary/network')
    || location.pathname.startsWith('/flyt')
    || location.pathname === '/search';

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className={isDarkRoute ? 'bg-black' : ''}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-1">
        <button
          type="button"
          onClick={handleBack}
          className={`inline-flex items-center gap-1.5 text-sm font-bold transition group ${
            isDarkRoute
              ? 'text-gray-300 hover:text-white'
              : 'text-gray-600 hover:text-black'
          }`}
          aria-label="Go back"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition" />
          Back
        </button>
      </div>
    </div>
  );
};

export default BackButton;
